import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, rotateX: -15 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ 
        y: -10, 
        rotateX: 5, 
        rotateY: 5,
        scale: 1.02,
        transition: { duration: 0.3 }
      }}
      className="group relative bg-card rounded-2xl overflow-hidden shadow-3d hover:shadow-accent transform-3d perspective-1000"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Project Image */}
      <div className="relative h-64 overflow-hidden">
        <Image
          src={project?.image}
          alt={project?.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Project Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-accent/20 backdrop-blur-md text-accent text-xs font-medium rounded-full border border-accent/30">
            {project?.category}
          </span>
        </div>

        {/* Project Status */}
        {project?.status && (
          <div className="absolute top-4 right-4">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              project?.status === 'Live' ?'bg-success/20 text-success border border-success/30' :'bg-warning/20 text-warning border border-warning/30'
            }`}>
              {project?.status}
            </span>
          </div>
        )}

        {/* Hover Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          className="absolute inset-0 bg-primary/60 backdrop-blur-sm flex items-center justify-center"
        >
          {/* <Button
            variant="default"
            size="sm"
            iconName="ExternalLink"
            iconPosition="right"
            className="bg-accent text-accent-foreground hover:bg-accent/90"
          >
            View Project
          </Button> */}
        </motion.div>
      </div>
      {/* Project Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-bold text-foreground group-hover:text-accent transition-colors duration-300">
            {project?.title}
          </h3>
          <div className="flex items-center space-x-1">
            {project?.technologies?.slice(0, 3)?.map((tech, idx) => (
              <div
                key={idx}
                className="w-6 h-6 bg-muted rounded-full flex items-center justify-center"
                title={tech}
              >
                <Icon name="Code" size={12} className="text-muted-foreground" />
              </div>
            ))}
          </div>
        </div>

        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {project?.description}
        </p>

        {/* Project Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={12} />
              <span>{project?.duration}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={12} />
              <span>{project?.teamSize}</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {[...Array(5)]?.map((_, i) => (
              <Icon
                key={i}
                name="Star"
                size={12}
                className={i < project?.rating ? 'text-warning fill-current' : 'text-muted-foreground'}
              />
            ))}
          </div>
        </div>

        {/* Technology Stack */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project?.technologies?.slice(0, 4)?.map((tech, idx) => (
            <span
              key={idx}
              className="px-2 py-1 bg-muted/50 text-muted-foreground text-xs rounded-md"
            >
              {tech}
            </span>
          ))}
          {project?.technologies?.length > 4 && (
            <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md">
              +{project?.technologies?.length - 4} more
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-1">
          <Link to={`/portfolio/${project?.id}`} className="flex-1">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              iconName="Eye"
              iconPosition="left"
              className="border-accent/30 text-accent hover:bg-accent"
            >
              Case Study
            </Button>
          </Link>
          {/* {project?.liveUrl && (
            <Button
              variant="ghost"
              size="sm"
              iconName="ExternalLink"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => window.open(project?.liveUrl, '_blank')}
            />
          )} */}
        </div>
      </div>
      {/* 3D Depth Effect */}
      <div className="absolute inset-0 rounded-2xl border border-border/50 pointer-events-none group-hover:border-accent/30 transition-colors duration-300" />
    </motion.div>
  );
};

export default ProjectCard;